
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import {
  FiBook,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiEdit,
  FiTrash2,
  FiCheckCircle,
} from "react-icons/fi";
import { baseApiURL } from "../../baseUrl";

const frequencies = [
  "Daily",
  "Weekly",
  "Fortnightly",
  "Monthly",
  "Quarterly",
  "Yearly",
  "Other",
];

const initialForm = {
  title: "",
  language: "English",
  frequency: "Daily",
  vendor: "",
  publisher: "",
  copies: 1,
  remarks: "",
  lastReceivedOn: "",
  isActive: true,
};

const NewsPaper = () => {
  const [papers, setPapers] = useState([]);
  const [formData, setFormData] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [selectedTab, setSelectedTab] = useState("list");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchPapers = () => {
    setLoading(true);
    axios
      .get(`${baseApiURL()}/newspaper`)
      .then((res) => {
        if (res.data.success) {
          setPapers(res.data.newspapers || []);
        } else {
          toast.error(res.data.message || "Unable to load newspapers");
        }
      })
      .catch((error) =>
        toast.error(error.response?.data?.message || "Unable to load newspapers")
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPapers();
  }, []);

  const resetForm = () => {
    setFormData(initialForm);
    setEditingId(null);
  };

  const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString();
  };

  const filteredPapers = useMemo(() => {
    return papers
      .filter((paper) => {
        if (statusFilter === "active" && !paper.isActive) return false;
        if (statusFilter === "inactive" && paper.isActive) return false;
        const term = searchTerm.toLowerCase();
        if (!term) return true;
  return (
          paper.title?.toLowerCase().includes(term) ||
          paper.vendor?.toLowerCase().includes(term) ||
          paper.language?.toLowerCase().includes(term)
        );
      })
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  }, [papers, searchTerm, statusFilter]);

  const stats = useMemo(() => {
    const total = papers.length;
    const active = papers.filter((paper) => paper.isActive).length;
    const dueSoon = papers.filter((paper) => {
      if (!paper.nextIssueDueOn) return false;
      const diff =
        (new Date(paper.nextIssueDueOn).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
      return diff <= 3;
    }).length;
    return { total, active, dueSoon };
  }, [papers]);

  const handleSubmit = () => {
    if (!formData.title.trim()) {
      return toast.error("Title is required");
    }
    setSaving(true);
    const payload = {
      ...formData,
      copies: Number(formData.copies) || 1,
      isActive: Boolean(formData.isActive),
      language: formData.language || "English",
      frequency: formData.frequency || "Daily",
    };
    if (payload.lastReceivedOn) {
      payload.lastReceivedOn = new Date(payload.lastReceivedOn).toISOString();
    } else {
      delete payload.lastReceivedOn;
    }

    const request = editingId
      ? axios.put(`${baseApiURL()}/newspaper/${editingId}`, payload)
      : axios.post(`${baseApiURL()}/newspaper`, payload);

    request
      .then((res) => {
        if (res.data.success) {
          toast.success(res.data.message || "Saved");
          resetForm();
          setSelectedTab("list");
          fetchPapers();
        } else {
          toast.error(res.data.message || "Something went wrong");
        }
      })
      .catch((error) => toast.error(error.response?.data?.message || "Save failed"))
      .finally(() => setSaving(false));
  };

  const handleEdit = (paper) => {
    setEditingId(paper._id);
    setFormData({
      title: paper.title || "",
      language: paper.language || "English",
      frequency: paper.frequency || "Daily",
      vendor: paper.vendor || "",
      publisher: paper.publisher || "",
      copies: paper.copies || 1,
      remarks: paper.remarks || "",
      lastReceivedOn: paper.lastReceivedOn
        ? new Date(paper.lastReceivedOn).toISOString().split("T")[0]
        : "",
      isActive: paper.isActive ?? true,
    });
    setSelectedTab("form");
  };

  const handleDelete = (paper) => {
    if (!window.confirm(`Delete ${paper.title}?`)) return;
    axios
      .delete(`${baseApiURL()}/newspaper/${paper._id}`)
      .then((res) => {
        if (res.data.success) {
          toast.success("Entry deleted");
          fetchPapers();
        } else {
          toast.error(res.data.message || "Deletion failed");
        }
      })
      .catch((error) => toast.error(error.response?.data?.message || "Deletion failed"));
  };

  const handleMarkReceived = (paper) => {
    axios
      .post(`${baseApiURL()}/newspaper/${paper._id}/receive`, {
        receivedOn: new Date().toISOString(),
      })
      .then((res) => {
        if (res.data.success) {
          toast.success("Receipt recorded");
          fetchPapers();
        } else {
          toast.error(res.data.message || "Unable to update");
        }
      })
      .catch((error) => toast.error(error.response?.data?.message || "Unable to update"));
  };

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs overflow-hidden">
        <div className="bento-header-banner flex flex-wrap gap-4 justify-between items-center px-8 py-6">
          <div className="flex items-center gap-3">
            <div className="bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-800 p-2 rounded-xl text-indigo-600 dark:text-indigo-400">
              <FiBook className="text-xl" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">Newspapers & Magazines</h1>
              <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
                Track subscriptions, arrivals, and circulation status.
              </p>
            </div>
          </div>
          <div className="flex space-x-1.5 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
            {["list", "form"].map((tab) => (
              <button
                key={tab}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-bold capitalize transition cursor-pointer ${
                  selectedTab === tab
                    ? "bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-xs"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                }`}
                onClick={() => setSelectedTab(tab)}
              >
                {tab === "list" ? "View Records" : editingId ? "Edit Entry" : "Add Entry"}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Titles</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{stats.total}</p>
            </div>
            <div className="p-4 rounded-xl border border-emerald-100 dark:border-emerald-800/60 bg-emerald-50/60 dark:bg-emerald-950/40">
              <p className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Active Subscriptions</p>
              <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300 mt-1">{stats.active}</p>
            </div>
            <div className="p-4 rounded-xl border border-amber-100 dark:border-amber-800/60 bg-amber-50/60 dark:bg-amber-950/40">
              <p className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">Due in next 3 days</p>
              <p className="text-2xl font-bold text-amber-700 dark:text-amber-300 mt-1">{stats.dueSoon}</p>
            </div>
          </div>

          {selectedTab === "list" && (
            <div className="space-y-4">
              <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={fetchPapers}
                    className="px-3.5 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-1.5 transition cursor-pointer shadow-xs"
                    disabled={loading}
                  >
                    <FiRefreshCw className={loading ? "animate-spin" : ""} />
                    Refresh
                  </button>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-xs font-semibold text-slate-900 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="all">All statuses</option>
                    <option value="active">Active only</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="relative flex-1">
                  <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by title, language, vendor..."
                    className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-xs">
                  <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider">
                    <tr>
                      <th className="px-4 py-3 text-left">Title</th>
                      <th className="px-4 py-3 text-left">Frequency</th>
                      <th className="px-4 py-3 text-left">Vendor</th>
                      <th className="px-4 py-3 text-left">Last Received</th>
                      <th className="px-4 py-3 text-left">Next Due</th>
                      <th className="px-4 py-3 text-left">Copies</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
                    {filteredPapers.length === 0 && (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-4 py-6 text-center text-slate-400 text-xs"
                        >
                          {loading ? "Loading records..." : "No entries match the filters."}
                        </td>
                      </tr>
                    )}
                    {filteredPapers.map((paper) => (
                      <tr key={paper._id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
                        <td className="px-4 py-3">
                          <p className="font-bold text-slate-900 dark:text-white">{paper.title}</p>
                          <p className="text-[11px] text-slate-400">
                            {paper.language} • {paper.publisher || "Publisher N/A"}
                          </p>
                        </td>
                        <td className="px-4 py-3">{paper.frequency}</td>
                        <td className="px-4 py-3">
                          {paper.vendor || "—"}
                        </td>
                        <td className="px-4 py-3">
                          {formatDate(paper.lastReceivedOn)}
                        </td>
                        <td className="px-4 py-3">
                          {formatDate(paper.nextIssueDueOn)}
                        </td>
                        <td className="px-4 py-3">{paper.copies || 1}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-0.5 rounded-lg text-[10px] font-bold border ${
                              paper.isActive
                                ? "bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800"
                                : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700"
                            }`}
                          >
                            {paper.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleMarkReceived(paper)}
                              className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 transition p-1 cursor-pointer"
                              title="Mark latest issue received"
                            >
                              <FiCheckCircle size={16} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleEdit(paper)}
                              className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 transition p-1 cursor-pointer"
                              title="Edit"
                            >
                              <FiEdit size={16} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(paper)}
                              className="text-rose-600 dark:text-rose-400 hover:text-rose-700 transition p-1 cursor-pointer"
                              title="Delete"
                            >
                              <FiTrash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {selectedTab === "form" && (
            <div className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 space-y-4">
              <div className="flex items-center gap-2 text-slate-900 dark:text-white font-bold text-xs uppercase tracking-wider">
                <FiPlus className="text-indigo-600 dark:text-indigo-400" />
                <h2>
                  {editingId ? "Update Subscription" : "Add New Subscription"}
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1 block">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Eg: The Hindu"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1 block">
                    Language
                  </label>
                  <input
                    type="text"
                    value={formData.language}
                    onChange={(e) =>
                      setFormData({ ...formData, language: e.target.value })
                    }
                    className="w-full px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1 block">
                    Frequency
                  </label>
                  <select
                    value={formData.frequency}
                    onChange={(e) =>
                      setFormData({ ...formData, frequency: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {frequencies.map((freq) => (
                      <option key={freq} value={freq}>
                        {freq}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1 block">
                    Vendor / Agency
                  </label>
                  <input
                    type="text"
                    value={formData.vendor}
                    onChange={(e) =>
                      setFormData({ ...formData, vendor: e.target.value })
                    }
                    className="w-full px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1 block">
                    Publisher
                  </label>
                  <input
                    type="text"
                    value={formData.publisher}
                    onChange={(e) =>
                      setFormData({ ...formData, publisher: e.target.value })
                    }
                    className="w-full px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1 block">
                    Copies
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.copies}
                    onChange={(e) =>
                      setFormData({ ...formData, copies: e.target.value })
                    }
                    className="w-full px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1 block">
                    Last Received On
                  </label>
                  <input
                    type="date"
                    value={formData.lastReceivedOn}
                    onChange={(e) =>
                      setFormData({ ...formData, lastReceivedOn: e.target.value })
                    }
                    className="w-full px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="isActive"
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                    className="h-4 w-4 text-indigo-600 rounded border-slate-300 dark:border-slate-700"
                  />
                  <label htmlFor="isActive" className="text-xs font-semibold text-slate-700 dark:text-slate-300 cursor-pointer">
                    Subscription active
                  </label>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1 block">
                  Remarks
                </label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) =>
                    setFormData({ ...formData, remarks: e.target.value })
                  }
                  rows={3}
                  className="w-full px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 text-xs resize-none"
                  placeholder="Eg: Stored in reading hall rack 2"
                />
              </div>

              <div className="flex flex-wrap gap-2.5 justify-end pt-2">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 text-xs font-bold rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                  disabled={saving}
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={saving}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl shadow-xs transition cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
                >
                  {saving && <FiRefreshCw className="animate-spin" />}
                  {editingId ? "Update Entry" : "Add Entry"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsPaper;